/**
 * @ngdoc directive
 * @name Player
 * @description Wraps the player iframe as a component directive which can be passed a guid.
 * @param $timeout
 * @param $parse
 * @param $sce
 * @param environment
 * @param User
 * @param $httpParamSerializer
 * @param $q
 * @returns {{restrict: string, scope: {guid: string}, templateUrl: string, link: Function}} <- DDO
 * @constructor
 */
export default function Player($timeout, $parse, $sce, environment, User, $httpParamSerializer, $q, $rootScope) {
  'ngInject';
  return {
    //Restrict to elements only
    priority: -1,
    restrict: "E",
    transclude: true,
    templateUrl: 'app/components/player/player.html',
    //Public api
    controller: function ($scope) {
      'ngInject';
      this.pdk = undefined;
      this.deferredCommand = [];
      /**
       * @name setCommand
       * @description Describe a command that needs to be run when the pdk is available. If the pdk is not available at execution time, then defer the call.
       * @param fn
       * @param args
       */
      let setCommand = (fn, args = []) => {

        //Check for pdk - Will be undefined when laoding new media.
        if (this.pdk) {
          //Exectue the command now.
          this.callCommand(fn, args);
        }
        else {
          //Create a deferred command.
          this.deferredCommand.push({fn, args});
        }
      };
      //Controller Methods.

      /**
       * @name callCommand
       * @description Executes the command within the pdk controller context, with args.
       * @param fn
       * @param args
       */
      this.callCommand = (fn, args) => {
        this.pdk.controller[fn].call(this.pdk.controller, ...args);
      };

      /**
       * @name On
       * @description Adds an event listener to the pdk
       * @param event
       * @param callback
       */
      this.on = (event, callback) => {
        let on = true,
          callbackWrapper = function () {
            if (on) {
              callback(...arguments);
            }
          };
        setCommand('addEventListener', [event, callbackWrapper]);
        return function () {
          on = false;
          setCommand('removeEventListener', [event]);
        };
      };

      /**
       * @name seek
       * @description Jump to a specific place in the playback.
       * @param position
       */
      this.seek = (position) => {
        setCommand('seekToPosition', [position]);
      };

      /**
       * @name play
       * @description Start media playback
       */
      this.play = (offset = 0) => {
        //Using timeout in our methods ensure that the method will execute AFTER the digest cycle. This allows the guid to be changed and the player to start reloading BEFORE the command is set.
        if (offset === 0) {
          setCommand('clickPlayButton');
        }
        else {
          setCommand('clickPlayButton');
          window.setTimeout(()=>{
            this.seek(offset);
            console.log('seek');
          }, 1000)

        }
        $scope.moveToPlayer();
      };

      /**
       * @name pause
       * @description Pause/stop media playback
       */
      this.pause = () => {
        //Putting our arguments in the array allows us to ... spread them later.
        setCommand('pause', [true]);
      };
    },
    //Controller name inside of template
    controllerAs: "player",
    //Isolated scope
    scope: {
      //Public api name
      name: '@',
      //Media unique id
      guid: '='
    },
    compile: function () {
      return {
        pre: function ($scope, $element, $attributes, ctrl) {
          //Setter for public api on parent scope.
          let setPlayerName = $parse($scope.name).assign;
          //Sets the value of name as a variable on the parent scope with the controller object. This allows us to use the controllers public api within scopes.
          setPlayerName($scope.$parent, ctrl);
          $scope.id = Math.random().toString(36).slice(2);
        },
        post: function ($scope, $element, $attributes, ctrl) {
          /**
           * @name buildUrl
           * @description Builds the sometimes complex player url.
           */
          function buildUrl() {
            let base = environment.player.url,
              guid = $scope.guid,
              token = User.loggedIn() ? User.currentUser.playerToken : environment.player.previewToken,
              params = {
                token,
                guid
              },
              paramStr = $httpParamSerializer(params);
            return `${base}?${paramStr}`;
          }

          //The Iframe that the player and pdk live in.
          let playerFrame = $element.find('iframe')[0];

          //On the load event of the player iframe
          playerFrame.onload = function () {
            //Access the player PDK
            let pdk = playerFrame.contentWindow.$pdk;
            if (pdk) {
              //Initialize the pdk so the player and the pdk are connected
              pdk.initialize();
              //Tell this player directive that the PDK/Player is ready to talk.
              ctrl.api.resolve(pdk);
            }
            else {
              ctrl.api.reject();
            }
          };

          $scope.moveToPlayer = function () {
            TweenLite.to(window, 1, {
              scrollTo: {
                y: $element.findPos().top,
                autoKill: false
              }
            });
          };

          //On the setting or changing of the guid scope variable
          //TODO - user attr.$observe to see if that speeds up the player ctrl set digest cycle.
          $scope.$watch('guid', function () {
            //Defer the api object
            ctrl.api = $q.defer();
            //Set the pdk to nothing so we will know to defer commands.
            ctrl.pdk = undefined;
            //When the pdk is ready inside of the iframe, give it to the controller and run deferred commads
            ctrl.api.promise.then((pdk) => {
              ctrl.pdk = pdk;
              $rootScope.$emit('playerReady', ctrl);
              //If there is a command waiting to be executed
              if (ctrl.deferredCommand.length > 0) {
                ctrl.deferredCommand.map(function (command) {
                  //Create fn and args vars from the command Object
                  let {fn, args} = command;
                  //Run the command
                  ctrl.callCommand(fn, args);
                });

                ctrl.deferredCommand = [];
              }
            }, ()=> {
              console.error('Player cannot be loaded because PDK is not available.');
            });

            /**
             * @name playerUrl
             * @description The full tokenized url for the player.
             */
            $scope.playerUrl = $sce.trustAsResourceUrl(buildUrl());
          });
        }
      };
    }
  };
}



/** WEBPACK FOOTER **
 ** ./src/app/components/player/player.directive.js
 **/